import React from 'react';
import { useHover } from 'react-aria';
import AnimatedTooltip from '../ui/AnimatedTooltip';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * Basket - Organization-grouped prop basket with fill states
 * 
 * Uses sprite sheets to show empty/medium/full states.
 * Each sprite sheet has 3 states arranged horizontally: [empty | medium | full]
 * 
 * @param {string} orgId - Organization identifier
 * @param {string} orgName - Organization display name
 * @param {number} itemCount - Number of items in basket
 * @param {number} spriteVariant - Which sprite sheet to use (1-4)
 * @param {function} onClick - Click handler for basket interaction
 */
const Basket = React.memo(({ 
  orgId, 
  orgName, 
  itemCount = 0, 
  spriteVariant = 1,
  onClick 
}) => {
  const { t } = useLanguage();
  const { hoverProps, isHovered } = useHover({});
  
  const getFillState = () => {
    if (itemCount === 0) return 'empty';
    if (itemCount <= 3) return 'medium';
    return 'full';
  };

  const getSpritePosition = () => {
    const state = getFillState();
    switch(state) {
      case 'empty': return '0%';
      case 'medium': return '50%';
      case 'full': return '100%';
      default: return '0%';
    }
  };

  const fillState = getFillState();
  const spritePosition = getSpritePosition();
  const spriteImage = `/images/bottom_shell/BasketStates_Sprite_${spriteVariant}.png`;
  const isEmpty = itemCount === 0;
  const buttonRef = React.useRef(null);

  const basketContent = (
    <div
      {...hoverProps}
      className="relative flex-shrink-0 pointer-events-auto"
      style={{ 
        height: 'clamp(100px, 20vw, 150px)', 
        width: 'clamp(100px, 20vw, 150px)'
      }}
    >
      <div 
        className="absolute inset-0 transition-transform pointer-events-none"
        style={{
          backgroundImage: `url('${spriteImage}')`,
          backgroundSize: '300% auto',
          backgroundPosition: `${spritePosition} bottom`,
          backgroundRepeat: 'no-repeat',
          transform: isHovered ? 'scale(1.1)' : 'scale(1)'
        }}
      />

      <button
        ref={buttonRef}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isEmpty) {
            onClick(orgId, e);
          }
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        disabled={isEmpty}
        className={`absolute bottom-0 left-0 right-0 h-[65%] outline-none focus:outline-none active:outline-none rounded-lg ${
          isEmpty ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        }`}
        aria-label={`${orgName} basket with ${itemCount} items${isEmpty ? ' (empty)' : ''}`}
        style={{ minHeight: '50px' }}
        tabIndex={isEmpty ? -1 : 0}
      />

      <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1 sm:gap-1.5 pl-2 sm:pl-3 pr-0.5 sm:pr-1 py-0.5 bg-gradient-to-b from-slate-800 to-slate-900 rounded-full shadow-lg border border-slate-700 pointer-events-none" style={{ maxWidth: '90%', bottom: 'clamp(8px, 2vw, 12px)' }}>
        <span className="text-[10px] sm:text-xs text-white font-semibold whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: 'clamp(9px, 1.8vw, 12px)' }}>
          {orgName}
        </span>
        
        <div className="bg-blue-600 text-white text-[10px] sm:text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center font-semibold shadow-sm flex-shrink-0">
          {itemCount}
        </div>
      </div>
    </div>
  );

  if (isEmpty) {
    return (
      <AnimatedTooltip 
        content={t('conveyor.addProps')}
        placement="top"
        delay={300}
        triggerRef={buttonRef}
      >
        {basketContent}
      </AnimatedTooltip>
    );
  }

  return basketContent;
});

export default Basket;
